'use client';

import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { type PropsWithChildren, type ReactNode, useEffect, useRef } from 'react';
import withSidebar from '@/components/hoc/withSidebar';
import SidebarContent from '@/components/navigation/Sidebar/SidebarContent.client';
import SidebarHeader from '@/components/navigation/Sidebar/SidebarHeader.client';
import type { TWithSidebar } from '@/lib/interfaces/hoc/withSidebar.types';
import type { TWithClassName } from '@/lib/interfaces/shared';

type TSidebarChildren = ReactNode | ((toggle: (value: boolean) => void) => ReactNode);

type TSidebar = Omit<PropsWithChildren<TWithSidebar>, 'children'> &
  TWithClassName & {
    withLogo?: boolean;
    children?: TSidebarChildren;
  };

const Sidebar = ({ opened, toggle, withLogo = true }: TSidebar) => {
  const mounted = useRef(false);
  const openBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    mounted.current = true;
    const html = document.documentElement;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) return;

    if (opened) {
      const y = window.scrollY;
      document.body.style.top = `-${y}px`;
      document.body.style.height = '100%';
      requestAnimationFrame(() => {
        html.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        html.classList.add('menu-open');
        document.body.classList.add('menu-open');
      });
    } else {
      html.style.overflow = '';
      document.body.style.position = '';
      document.body.style.height = '';
      const top = document.body.style.top;
      document.body.style.top = '';
      requestAnimationFrame(() => {
        if (top) window.scrollTo(0, -parseInt(top, 10));
        // Prevent focus returning to the burger button on close (iOS Safari)
        openBtnRef.current?.blur();
      });
      if (mounted.current) {
        html.classList.add('menu-closing');
        setTimeout(() => html.classList.remove('menu-closing'), 300);
      }
    }
    return () => {
      mounted.current = false;
      const top = document.body.style.top;
      html.style.overflow = '';
      document.body.style.position = '';
      document.body.style.height = '';
      document.body.style.top = '';
      html.classList.remove('menu-open');
      document.body.classList.remove('menu-open');
      if (top) window.scrollTo(0, -parseInt(top, 10));
    };
  }, [opened]);

  return (
    <>
      <button
        type='button'
        onClick={() => toggle(true)}
        ref={openBtnRef}
        className='-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-gray-400 focus:outline-none'
        aria-label='Open navigation menu'
        aria-expanded={opened}
        aria-controls='mobile-menu'
      >
        <span className='sr-only'>Open main menu</span>
        <Bars3Icon aria-hidden='true' className='h-6 w-6' />
      </button>

      <Dialog open={opened} onClose={toggle} className='lg:hidden'>
        <div className='fixed inset-0 z-40 bg-black/70' style={{ transition: 'none' }} />
        <DialogPanel
          key='sidebar-panel'
          id='mobile-menu'
          className='fixed inset-y-0 right-0 z-50 h-[100dvh] w-full overflow-y-auto bg-background sm:max-w-sm sm:ring-1 sm:ring-gray-900/10 dark:sm:ring-gray-100/10'
          aria-label='Mobile navigation menu'
        >
          <div className='relative z-10 flex h-full flex-col bg-transparent'>
            <SidebarHeader onClose={toggle} withLogo={withLogo} />
            <SidebarContent onClose={toggle} />
          </div>
        </DialogPanel>
      </Dialog>
    </>
  );
};

Sidebar.displayName = 'Sidebar';

export default withSidebar(Sidebar);
